import { Box, Button, IconButton, Typography, useTheme, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import { mockTransactions } from "../../data/mockData";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useState, useEffect } from 'react';
import { apiGet } from '../../utils/apiUtils';
import { API_ENDPOINTS } from '../../config/api';
import { getUserData } from '../../services/authService';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMdScreen = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const navigate = useNavigate();

  const [leadCounts, setLeadCounts] = useState({
    sell: 0,
    rent: 0,
    jv: 0,
    investor: 0,
    agriculture: 0,
    document: 0,
    total: 0
  });

  const handleCardClick = (type) => {
    const userData = getUserData();
    const isAdmin = userData?.Role?.Name === "Admin";

    // Define routes based on user role and card type
    const routes = {
      rent: isAdmin ? '/listing/rent' : '/rent',
      investor: isAdmin ? '/listing/invest' : '/invest',
      sell: isAdmin ? '/listing/sell' : '/sell',
      jv: isAdmin ? '/listing/jointventure' : '/jointventure',
      agriculture: isAdmin ? '/listing/agriculture' : '/agriculture/yearly-contract',
      document: isAdmin ? '/documents-verification' : '/documents-verification'
    };

    if (routes[type]) {
      navigate(routes[type]);
    }
  };

  useEffect(() => {
    const fetchLeadCounts = async () => {
      try {
        const userData = getUserData();
        if (!userData || !userData.User_ID) {
          throw new Error('User not authenticated');
        }

        console.log('User Data:', userData);
        let response;

        // Check if user is admin by checking the Role object
        if (userData.Role && userData.Role.Name === "Admin") {
          console.log('User is Admin, fetching property counts');
          response = await apiGet('/api/leads/getAllProperty-Counts');
        } else {
          console.log('User is Dealer, fetching lead counts');
          response = await apiGet(`${API_ENDPOINTS.PROPERTIES.MAINPAGE_STATS}?User_ID=${userData.User_ID}`);
        }

        console.log('API Response:', response);

        if (response.success) {
          console.log('Setting lead counts:', response.result);
          setLeadCounts(response.result);
        } else {
          console.error('API returned error:', response.message);
        }
      } catch (error) {
        console.error('Error fetching counts:', error);
      }
    };

    fetchLeadCounts();
  }, []);

  // Log the current state whenever it changes
  useEffect(() => {
    console.log('Current lead counts state:', leadCounts);
  }, [leadCounts]);

  // Determine column spans based on screen size
  const firstRowSpan = isXsScreen ? 12 : (isSmScreen ? 6 : 4);
  const secondRowSpan = isXsScreen ? 12 : 6;

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box 
        display="flex" 
        flexDirection={isXsScreen ? "column" : "row"} 
        justifyContent="center" 
        alignItems={isXsScreen ? "flex-start" : "center"}
        gap={isXsScreen ? 2 : 0}
        mb={3}
      >
        <Header subtitle="Welcome to your dashboard" />
      </Box>

      {/* GRID & CHARTS */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows={isXsScreen ? "auto" : "140px"}
        gap="20px"
      >
        {/* Row 1: 3 Cards */}
        <Box
          gridColumn={`span ${firstRowSpan}`}
          backgroundColor={colors.primary[300]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="10px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          p={isXsScreen ? 2 : 0}
          minHeight="140px"
          onClick={() => handleCardClick('rent')}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.primary[400] } }}
        >
          <StatBox
            title={leadCounts.rent.toLocaleString()}
            subtitle="Total Rents"
            icon={
              <MeetingRoomOutlinedIcon
                sx={{ color: colors.primary[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={`span ${firstRowSpan}`}
          backgroundColor={colors.primary[300]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="10px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          p={isXsScreen ? 2 : 0}
          minHeight="140px"
          onClick={() => handleCardClick('investor')}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.primary[400] } }}
        >
          <StatBox
            title={leadCounts.investor.toLocaleString()}
            subtitle="Total Investors"
            icon={
              <AccountBalanceOutlinedIcon
                sx={{ color: colors.primary[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={`span ${firstRowSpan}`}
          backgroundColor={colors.primary[300]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="10px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          p={isXsScreen ? 2 : 0}
          minHeight="140px"
          onClick={() => handleCardClick('sell')}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.primary[400] } }}
        >
          <StatBox
            title={leadCounts.sell.toLocaleString()}
            subtitle="Total Sells"
            icon={
              <SellOutlinedIcon
                sx={{ color: colors.primary[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        {/* Row 2: 3 Cards */}
        <Box
          gridColumn={`span ${firstRowSpan}`}
          backgroundColor={colors.primary[300]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="10px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          p={isXsScreen ? 2 : 0}
          minHeight="140px"
          onClick={() => handleCardClick('jv')}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.primary[400] } }}
        >
          <StatBox
            title={leadCounts.jv.toLocaleString()}
            subtitle="Joint Ventures"
            icon={
              <HandshakeOutlinedIcon
                sx={{ color: colors.primary[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={`span ${firstRowSpan}`}
          backgroundColor={colors.primary[300]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="10px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          p={isXsScreen ? 2 : 0}
          minHeight="140px"
          onClick={() => handleCardClick('agriculture')}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.primary[400] } }}
        >
          <StatBox
            title={leadCounts.agriculture.toLocaleString()}
            subtitle="Agriculture"
            icon={
              <AgricultureOutlinedIcon
                sx={{ color: colors.primary[600], fontSize: "26px" }}
              />
            }
          />
        </Box>

        <Box
          gridColumn={`span ${firstRowSpan}`}
          backgroundColor={colors.primary[300]}
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius="10px"
          boxShadow="0 4px 8px rgba(0,0,0,0.1)"
          p={isXsScreen ? 2 : 0}
          minHeight="140px"
          onClick={() => handleCardClick('document')}
          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: colors.primary[400] } }}
        >
          <StatBox
            title={leadCounts.document.toLocaleString()}
            subtitle="Documents"
            icon={
              <DescriptionOutlinedIcon
                sx={{ color: colors.primary[600], fontSize: "26px" }}
              />
            }
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
